import { SUPPORTED_LOCALES, type SupportedLocale } from "@/lib/i18n/config";

type LocalePathnameMatch = {
  locale: SupportedLocale | null;
  pathnameWithoutLocale: string;
  hasLocalePrefix: boolean;
};

const SUPPORTED_LOCALE_SET = new Set<string>(SUPPORTED_LOCALES);

const ensureLeadingSlash = (pathname: string) => {
  if (!pathname) return "/";
  return pathname.startsWith("/") ? pathname : `/${pathname}`;
};

const normalizePathname = (pathname: string) => {
  const withLeadingSlash = ensureLeadingSlash(pathname);
  if (withLeadingSlash === "/") return withLeadingSlash;
  return withLeadingSlash.replace(/\/+$/, "") || "/";
};

export const getLocaleFromPathname = (
  pathname: string
): SupportedLocale | null => {
  const normalized = normalizePathname(pathname);
  const [firstSegment] = normalized.split("/").filter(Boolean);
  if (!firstSegment) {
    return null;
  }

  const lowerSegment = firstSegment.toLowerCase();
  return SUPPORTED_LOCALE_SET.has(lowerSegment)
    ? (lowerSegment as SupportedLocale)
    : null;
};

export const stripLocaleFromPathname = (
  pathname: string
): LocalePathnameMatch => {
  const normalized = normalizePathname(pathname);
  const locale = getLocaleFromPathname(normalized);

  if (!locale) {
    return {
      locale: null,
      pathnameWithoutLocale: normalized,
      hasLocalePrefix: false,
    };
  }

  const pathnameWithoutLocale =
    normalized === `/${locale}`
      ? "/"
      : normalized.slice(locale.length + 1) || "/";

  return {
    locale,
    pathnameWithoutLocale: ensureLeadingSlash(pathnameWithoutLocale),
    hasLocalePrefix: true,
  };
};
